// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import PDFViewer, { PDFViewerStyles } from "@eisinc/App/Components/PDFViewer/PDFViewer";
import Skeleton from "@eisinc/App/Components/Skeleton/Skeleton";
import { CustomAppState } from "@eisinc/App/Store/Reducers";
import { DocumentGroup } from "@eisinc/App/Store/Reducers/ProductDetails/state";
import { getProductDocumentGroups } from "@eisinc/App/Store/Selectors/ProductDetailsSelectors";
import { XProductModel } from "@eisinc/Overrides/Pages/ProductDetailsPage";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { IconProps } from "@insite/mobius/Icon";
import File from "@insite/mobius/Icons/File";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { css } from "styled-components";

type OwnProps = WidgetProps & HasProduct;

export interface ProductDetailsDocumentsStyles {
    wrapper?: InjectableCss;
    container?: GridContainerProps;
    documentGridItem?: GridItemProps;
    documentLink?: LinkPresentationProps;
    documentIcon?: IconProps;
    documentNameText?: TypographyPresentationProps;
    // XNG-Changes
    titleStyles?: TypographyPresentationProps;
    typeTitle?: TypographyPresentationProps;
    pfdStyles?: PDFViewerStyles;
}

export const documentsStyles: ProductDetailsDocumentsStyles = {
    wrapper: {
        css: css`
            padding: 10px 15px 30px;
        `,
    },
    documentGridItem: {
        width: [12, 12, 6, 6, 6],
    },
    documentIcon: {
        src: File,
        color: "text.link",
        css: css`
            margin-right: 10px;
        `,
    },
    documentNameText: {
        css: css`
            vertical-align: super;
        `,
    },
};

const styles = documentsStyles;

const ProductDetailsDocuments: React.FC<OwnProps> = (
    // XNG-Changes: read Product
    { product },
) => {
    // XNG-Changes: get grouped documnets
    const documentsGroupedByType = useAppSelector(state => getProductDocumentGroups(state as CustomAppState, product));

    // XNG-Change
    const { isLoading } = product as XProductModel;

    // XNG-Change
    if (isLoading) {
        return (
            <StyledWrapper {...styles.wrapper}>
                <Skeleton height="200px" />
            </StyledWrapper>
        );
    }

    // XNG-Changes: adding title. + using PDFViewer. + grouping
    return (
        <StyledWrapper {...styles.wrapper}>
            <Typography {...styles.titleStyles}>{translate("Documents")}</Typography>
            <GridContainer {...styles.container}>
                {!documentsGroupedByType || documentsGroupedByType.length === 0 ? (
                    <GridItem {...styles.documentGridItem}>{siteMessage("Custom_PDP_No_Documents_Found")}</GridItem>
                ) : (
                    <>
                        {documentsGroupedByType?.map((group: DocumentGroup) => (
                            <GridItem key={group.groupName} {...styles.documentGridItem}>
                                <Typography {...styles.typeTitle}>{group.groupName}</Typography>
                                {group.documents.map(document => (
                                    <Link
                                        href={document.filePath}
                                        target="_new"
                                        {...styles.documentLink}
                                        data-test-selector={`productDetails_${document.id}`}
                                        key={document.id}
                                    >
                                        <PDFViewer
                                            key={document.id.toString()}
                                            filePath={document.filePath}
                                            fileName={document.name}
                                            dataTestSelector={`productDetails_${document.id}`}
                                            asLink={true}
                                            newTab={false}
                                            // https://xngage.atlassian.net/browse/EIS-3913 - foce all attachments to be embedded
                                            extraQueryStringParams={{
                                                doNotEmbed: false,
                                            }}
                                            extendedStyles={styles.pfdStyles}
                                        />
                                    </Link>
                                ))}
                            </GridItem>
                        ))}
                    </>
                )}
            </GridContainer>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(ProductDetailsDocuments),
    definition: {
        displayName: "Documents",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
